import { useCallback, useMemo, useState } from "react";
import { useWorkspacesList } from "@api/workspace.api";
import { CloseTemplate } from "@common/types/closeTemplate";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useScope } from "@state/scopeContext.lib";
import {
    ApplyCloseTemplateWizardBody,
    type TemplateWizardVariant,
} from "./ApplyCloseTemplateWizardBody";

interface Props {
    open: boolean;
    onClose: () => void;
    /** Called instead of onClose when the wizard has completed successfully. */
    onApplyComplete?: () => void;
    closeTemplate?: CloseTemplate;
}

export const ApplyCloseTemplateDrawer = ({
    open,
    onClose,
    onApplyComplete,
    closeTemplate,
}: Props) => {
    const { organizationId } = useScope();
    const { data: workspaces } = useWorkspacesList({
        organizationId: organizationId ?? undefined,
        includeEntities: true,
    });

    const workspaceDataKey = useMemo(
        () =>
            workspaces
                ? workspaces
                      .map((ws) =>
                          [ws.id, ...(ws.entities ?? []).map((e) => e.id)].join(
                              ":",
                          ),
                      )
                      .join("|")
                : "pending",
        [workspaces],
    );

    const [step, setStep] = useState(1);
    // Owned here rather than in the wizard body so it survives the body
    // remount when `workspaceDataKey` changes from "pending" to the resolved
    // key — otherwise a variant chosen on step 1 would reset to null and
    // silently switch the user into the wrong flow (see PRO-10556).
    const [variant, setVariant] = useState<TemplateWizardVariant | null>(null);

    const handleDrawerClose = useCallback(() => {
        setStep(1);
        setVariant(null);
        onClose();
    }, [onClose]);

    return (
        <Drawer
            open={open}
            onClose={handleDrawerClose}
            size="fullScreen"
            noPadding
            noScroll
            headerAddonLeft="Apply template"
        >
            {open ? (
                <ApplyCloseTemplateWizardBody
                    key={workspaceDataKey}
                    closeTemplate={closeTemplate}
                    step={step}
                    setStep={setStep}
                    variant={variant}
                    setVariant={setVariant}
                    onClose={handleDrawerClose}
                    onApplyComplete={onApplyComplete}
                />
            ) : null}
        </Drawer>
    );
};
